import { SimpleDatePicker } from "@/components/DatePicker/SimpleDatePicker";
import ResponseMessage from "@/components/Error/ResponseMessage";
import { VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { handleApiError } from "@/lib/api/error";
import { defaultNS } from "@/lib/i18n";
import { queryClient } from "@/lib/queryClient";
import { FormField } from "@/routes/_staff/person/-components/form/FormField";
import { formatISODate } from "@/utils/date";
import { useMutation } from "@tanstack/react-query";
import { type ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { updateCertification } from "../-query/api";
import { certificationsKeys } from "../-query/key";

const schema = z.object({
  certificationId: z.string().min(1),
  certificationName: z.string().min(1),
  certificationParty: z.string().min(1),
  effectiveDate: z.string().min(1),
  expirationDate: z.string().nullable(),
  renewalDate: z.string().nullable(),
});

type Form = z.infer<typeof schema> & {
  errorMessages?: string;
  zodError: Record<string, string[] | undefined>;
};

type FormProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  data: {
    id: string;
    certificationHolderId: string;
  } & z.infer<typeof schema>;
};

function CertificationsEditDialog({ isOpen, onOpenChange, data }: FormProps) {
  const { i18n } = useTranslation();
  const tc = useCommonTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix:
      "pages.person.profile.educationsAndQualifications.certification.dialog.edit",
  });

  const [form, setForm] = useState<Form>({
    ...data,
    errorMessages: undefined,
    zodError: {},
  });

  const {
    certificationIdLabel,
    certificationNameLabel,
    certificationPartyLabel,
    effectiveDateLabel,
    expirationDateLabel,
    renewalDateLabel,
  } = {
    certificationIdLabel: t("certificationId"),
    certificationNameLabel: t("certificationName"),
    certificationPartyLabel: t("certificationParty"),
    effectiveDateLabel: t("effectiveDate"),
    expirationDateLabel: t("expirationDate"),
    renewalDateLabel: t("renewalDate"),
  };

  const [pending, setPending] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (form: z.infer<typeof schema>) =>
      updateCertification(data.id, {
        certificationId: form.certificationId,
        certificationName: form.certificationName,
        certificationParty: form.certificationParty,
        effectiveDate: form.effectiveDate,
        expirationDate: form.expirationDate ?? null,
        renewalDate: form.renewalDate ?? null,
        certificationHolderId: data.certificationHolderId,
      }),
    onSuccess: () => {
      onOpenChange(false);
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      setForm({
        ...form,
        errorMessages: message,
        zodError: {},
      });
    },
  });

  const handleSave = async () => {
    setForm({
      ...form,
      errorMessages: undefined,
      zodError: {},
    });
    setPending(true);
    const result = schema.safeParse(form);

    if (!result.success) {
      setForm({
        ...form,
        errorMessages: undefined,
        zodError: result.error.flatten().fieldErrors,
      });
      setPending(false);
      return;
    }

    try {
      await updateMutation.mutateAsync(result.data);
      // 更新成功後、certificationsのキャッシュを無効化してデータを再取得
      queryClient.invalidateQueries({
        queryKey: certificationsKeys.all,
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t("title", { resource: data.certificationName })}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => e.preventDefault()}>
          <VStack className="gap-5">
            <VStack className="gap-2">
              <Text className="font-semibold text-muted-foreground text-xs">
                {certificationIdLabel}
              </Text>
              <FormField
                fieldInput={
                  <Input
                    value={form.certificationId}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setForm({ ...form, certificationId: e.target.value })
                    }
                    width={250}
                  />
                }
                errorMessage={form.zodError.certificationId?.[0]}
              />
            </VStack>
            <VStack className="gap-2">
              <Text className="font-semibold text-muted-foreground text-xs">
                {certificationNameLabel}
              </Text>
              <FormField
                fieldInput={
                  <Input
                    value={form.certificationName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setForm({ ...form, certificationName: e.target.value })
                    }
                    width={250}
                  />
                }
                errorMessage={form.zodError.certificationName?.[0]}
              />
            </VStack>
            <VStack className="gap-2">
              <Text className="font-semibold text-muted-foreground text-xs">
                {certificationPartyLabel}
              </Text>
              <FormField
                fieldInput={
                  <Input
                    value={form.certificationParty}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setForm({ ...form, certificationParty: e.target.value })
                    }
                    width={250}
                  />
                }
                errorMessage={form.zodError.certificationParty?.[0]}
              />
            </VStack>
            <VStack className="gap-2">
              <Text className="font-semibold text-muted-foreground text-xs">
                {effectiveDateLabel}
              </Text>
              <FormField
                fieldInput={
                  <SimpleDatePicker
                    value={
                      form?.effectiveDate
                        ? new Date(form.effectiveDate)
                        : undefined
                    }
                    onChange={(date) => {
                      setForm({
                        ...form,
                        effectiveDate: date ? formatISODate(date) : "",
                      });
                    }}
                    width={250}
                  />
                }
                errorMessage={form.zodError.effectiveDate?.[0]}
              />
            </VStack>
            <VStack className="gap-2">
              <Text className="font-semibold text-muted-foreground text-xs">
                {expirationDateLabel}
              </Text>
              <FormField
                fieldInput={
                  <SimpleDatePicker
                    value={
                      form?.expirationDate
                        ? new Date(form.expirationDate)
                        : undefined
                    }
                    onChange={(date) => {
                      setForm({
                        ...form,
                        expirationDate: date ? formatISODate(date) : null,
                      });
                    }}
                    allowEmpty={true}
                    width={250}
                  />
                }
                errorMessage={form.zodError.expirationDate?.[0]}
              />
            </VStack>
            <VStack className="gap-2">
              <Text className="font-semibold text-muted-foreground text-xs">
                {renewalDateLabel}
              </Text>
              <FormField
                fieldInput={
                  <SimpleDatePicker
                    value={
                      form?.renewalDate ? new Date(form.renewalDate) : undefined
                    }
                    onChange={(date) => {
                      setForm({
                        ...form,
                        renewalDate: date ? formatISODate(date) : null,
                      });
                    }}
                    allowEmpty={true}
                    width={250}
                  />
                }
                errorMessage={form.zodError.renewalDate?.[0]}
              />
            </VStack>
            <ResponseMessage>{form.errorMessages}</ResponseMessage>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="ghost" disabled={pending}>
                  {tc("action.cancel")}
                </Button>
              </DialogClose>
              <Button variant="default" disabled={pending} onClick={handleSave}>
                {tc("action.save")}
              </Button>
            </DialogFooter>
          </VStack>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CertificationsEditDialog;
